import { DIV_ZERO_SENTINEL } from 'common/authoring_workflow/components/modals/AdvancedTotalingModal/helpers/formulaHelpers';

// ============================================================================
// string helpers
// ============================================================================

// escapes single quotes for safe use in soql string literals.
// "Don's Asset" -> "Don''s Asset"
export const escapeSoqlStringLiteral = (value: string | null | undefined): string => {
  if (!value) return '';
  return value.replace(/'/g, "''");
};

const isWhitespace = (char: string | undefined): boolean => {
  return char === ' ' || char === '\n' || char === '\t' || char === '\r';
};

// ============================================================================
// cursor navigation — these move an index pointer without changing the string
// ============================================================================

const skipWhitespaceBackwards = (str: string, index: number): number => {
  let cursor = index;
  while (cursor >= 0 && isWhitespace(str[cursor])) cursor--;
  return cursor;
};

const skipWhitespaceForwards = (str: string, index: number): number => {
  let cursor = index;
  while (cursor < str.length && isWhitespace(str[cursor])) cursor++;
  return cursor;
};

const skipRegexBackwards = (str: string, index: number, regex: RegExp): number => {
  let cursor = index;
  while (cursor >= 0 && regex.test(str[cursor])) cursor--;
  return cursor;
};

const skipRegexForwards = (str: string, index: number, regex: RegExp): number => {
  let cursor = index;
  while (cursor < str.length && regex.test(str[cursor])) cursor++;
  return cursor;
};

// jumps the cursor forward past a backtick-enclosed identifier like `Revenue (Net)`
const skipTicksForwards = (str: string, index: number): number => {
  let cursor = index + 1;
  while (cursor < str.length && str[cursor] !== '`') cursor++;
  if (cursor < str.length) cursor++;
  return cursor;
};

// jumps the cursor backward past a backtick-enclosed identifier
const skipTicksBackwards = (str: string, index: number): number => {
  let cursor = index - 1;
  while (cursor >= 0 && str[cursor] !== '`') cursor--;
  return cursor;
};

// ============================================================================
// parentheses balancing
// ============================================================================

// walks backward from a closing paren to find its matching opening paren,
// respecting nesting depth and skipping backtick-enclosed identifiers
const skipBalancedParensBackwards = (str: string, index: number): number => {
  let depth = 1;
  let cursor = index - 1;

  while (cursor >= 0 && depth > 0) {
    const ch = str[cursor];
    if (ch === '`') cursor = skipTicksBackwards(str, cursor);
    else if (ch === ')') depth++;
    else if (ch === '(') depth--;
    cursor--;
  }
  return cursor;
};

// walks forward from an opening paren to find its matching closing paren (simple — no tick awareness)
const skipBalancedParensSimpleForwards = (str: string, index: number): number => {
  let depth = 1;
  let cursor = index + 1;

  while (cursor < str.length && depth > 0) {
    if (str[cursor] === '(') depth++;
    else if (str[cursor] === ')') depth--;
    cursor++;
  }
  return cursor;
};

// same as above but skips over backtick-enclosed identifiers so their
// internal parens (like `Revenue (Net)`) don't mess up the depth count
const skipBalancedParensWithTicksForwards = (str: string, index: number): number => {
  let depth = 1;
  let cursor = index + 1;

  while (cursor < str.length && depth > 0) {
    const ch = str[cursor];
    if (ch === '`') {
      cursor = skipTicksForwards(str, cursor);
    } else {
      if (ch === '(') depth++;
      else if (ch === ')') depth--;
      cursor++;
    }
  }
  return cursor;
};

// ============================================================================
// operand extraction — finds the left/right side of a division operator
// ============================================================================

const REGEX_IDENTIFIER = /[a-zA-Z0-9_]/;
const REGEX_IDENTIFIER_WITH_DOT = /[a-zA-Z0-9_.]/;
const REGEX_ALPHA_UNDERSCORE = /[a-zA-Z_]/;
const REGEX_NUMBER_WITH_DOT = /[0-9.]/;

// scans backward from a division operator to isolate the left operand.
// handles parenthesized groups, backtick-wrapped names, function calls, and plain numbers.
const getLeftOperand = (str: string, index: number): { text: string; start: number } => {
  let cursor = skipWhitespaceBackwards(str, index - 1);
  if (cursor < 0) return { text: '', start: 0 };

  const endIndex = cursor;
  let startIndex = cursor;
  const lastChar = str[cursor];

  if (lastChar === ')') {
    // parenthesized group or function call like sum(...)
    cursor = skipBalancedParensBackwards(str, cursor);
    cursor = skipWhitespaceBackwards(str, cursor);
    cursor = skipRegexBackwards(str, cursor, REGEX_IDENTIFIER);
    startIndex = cursor + 1;
  } else if (lastChar === '`') {
    startIndex = skipTicksBackwards(str, cursor);
  } else {
    cursor = skipRegexBackwards(str, cursor, REGEX_IDENTIFIER_WITH_DOT);
    startIndex = cursor + 1;
  }

  return { text: str.substring(startIndex, endIndex + 1), start: startIndex };
};

// scans forward from a division operator to isolate the right operand.
// handles unary signs (e.g., / -B), parenthesized groups, function calls, and plain numbers.
const getRightOperand = (str: string, index: number): { text: string; end: number } => {
  let cursor = skipWhitespaceForwards(str, index + 1);
  if (cursor >= str.length) return { text: '', end: str.length };

  const startIndex = cursor;

  // handle unary +/- on the denominator
  if (str[cursor] === '-' || str[cursor] === '+') {
    cursor++;
    cursor = skipWhitespaceForwards(str, cursor);
  }

  const firstChar = str[cursor];

  if (firstChar === '(') {
    cursor = skipBalancedParensSimpleForwards(str, cursor);
  } else if (firstChar === '`') {
    cursor = skipTicksForwards(str, cursor);
  } else if (REGEX_ALPHA_UNDERSCORE.test(firstChar)) {
    cursor = skipRegexForwards(str, cursor, REGEX_IDENTIFIER);
    const lookahead = skipWhitespaceForwards(str, cursor);
    if (lookahead < str.length && str[lookahead] === '(') {
      cursor = skipBalancedParensSimpleForwards(str, lookahead);
    }
  } else {
    cursor = skipRegexForwards(str, cursor, REGEX_NUMBER_WITH_DOT);
  }

  return { text: str.substring(startIndex, cursor), end: cursor };
};

// ============================================================================
// division finder — locates top-level `/` operators (not inside parens or backticks)
// ============================================================================

const findTopLevelDivision = (str: string, startIndex: number): number => {
  let cursor = startIndex;

  while (cursor < str.length) {
    const ch = str[cursor];
    if (ch === '`') cursor = skipTicksForwards(str, cursor);
    else if (ch === '(') cursor = skipBalancedParensWithTicksForwards(str, cursor);
    else if (ch === '/') return cursor;
    else cursor++;
  }

  return -1;
};

// ============================================================================
// safe division wrapping
// ============================================================================

// wraps a single division in a case statement that handles divide-by-zero:
// - 0/0 -> 0 (irrelevant group, no data)
// - X/0 -> sentinel value (real divide-by-zero error)
// - X/Y -> normal division via nullif
const buildSafeDivisionSoql = (numerator: string, denominator: string): string => {
  return `case(${denominator} = 0, case(${numerator} = 0, 0, true, ${DIV_ZERO_SENTINEL}), true, ${numerator} / nullif(${denominator}, 0))`;
};

// recursively processes parenthetical groups first (bottom-up PEMDAS).
// if a formula is `A / (B / C)`, we must protect `B / C` before the outer division.
const protectNestedGroups = (formula: string): string => {
  let result = '';
  let cursor = 0;

  while (cursor < formula.length) {
    const ch = formula[cursor];

    if (ch === '`') {
      const end = skipTicksForwards(formula, cursor);
      result += formula.substring(cursor, end);
      cursor = end;
    } else if (ch === '(') {
      const end = skipBalancedParensWithTicksForwards(formula, cursor);
      const inner = formula.substring(cursor + 1, end - 1);
      result += '(' + protectDivisions(inner) + ')';
      cursor = end;
    } else {
      result += ch;
      cursor++;
    }
  }

  return result;
};

// iteratively finds and wraps top-level divisions left-to-right.
// restarts from index 0 after each wrap because the string indices shift.
const protectTopLevelDivisions = (formula: string): string => {
  let result = formula;
  let divIndex = findTopLevelDivision(result, 0);

  while (divIndex !== -1) {
    const left = getLeftOperand(result, divIndex);
    const right = getRightOperand(result, divIndex);

    if (left.text && right.text) {
      const safe = buildSafeDivisionSoql(left.text.trim(), right.text.trim());
      result = result.substring(0, left.start) + safe + result.substring(right.end);
      divIndex = findTopLevelDivision(result, 0);
    } else {
      divIndex = findTopLevelDivision(result, divIndex + 1);
    }
  }

  return result;
};

// main entry point: makes a formula's divisions safe for soql execution.
// two phases: protect nested groups first (PEMDAS), then top-level divisions.
export const protectDivisions = (formula: string): string => {
  const nestedProtected = protectNestedGroups(formula);
  return protectTopLevelDivisions(nestedProtected);
};
